<script setup lang="ts">
import type { CheckboxValueType } from "element-plus";
import { computed, ref, watch } from "vue";
import {
  FormDefinition,
  FormElementBase,
  FormElementRule,
  FormElementRuleType,
  LoginAccountRuleCondition,
  LoginAccountRuleType,
  RuleConditionBase,
  RuleConditionType,
  SectionAssignables,
  StatusBasedConditionType,
  SysOrgRoleName,
  WorkflowDefinition,
  WorkflowStatusRuleCondition,
} from "~/api";
import {
  loginAccountConditionDisplayNames,
  statusConditionDisplayNames,
} from "~/utils/enumDisplayNames";
import { showError } from "~/utils/handleAPIError";
import {
  isLoginAccountBasedCondition,
  isWorkflowStatusBasedCondition,
} from "~/utils/typeGuards";

export interface EditCondition {
  rule: FormElementRule;
  condition: RuleConditionBase;
}

const emit = defineEmits<{
  (e: "update:rules", value: FormElementRule[]): void;
  (e: "update:editing-condition", value?: EditCondition): void;
}>();

const props = defineProps<{
  form: FormDefinition;
  workflowDefinition: WorkflowDefinition;
  sectionAssignables?: SectionAssignables;
  editingCondition?: EditCondition;
}>();

/** Adding a Rule */
const ruleConditionType = RuleConditionType.WORKFLOW_STATUS_BASED;

/** the list of form elements selected for a rule to be applied to */
const _applicableElementGuids = ref<string[]>([]);
const applicableElements = computed<FormElementBase[]>(() =>
  props.form.components.filter((c) =>
    _applicableElementGuids.value.includes(c.guid),
  ),
);

/** the workflow status which the rule depends on */
const _conditionWorkflowStatus = ref<string>();
const statusOptions = ref<{ l: string; v: string }[]>(
  props.workflowDefinition.statusList
    ?.filter((s) => s.label !== null && s.code !== null)
    // using ! because we filtered nulls above
    .map((s) => ({ l: s.label!, v: s.code! })) ?? [
    { l: "Started", v: "Started" },
    { l: "In Progress", v: "In Progress" },
    { l: "Completed", v: "Completed" },
  ],
);

/** the type of the rule (e.g. hide, readonly) */
const ruleType = ref<FormElementRuleType>();
/** the type of the condition (e.g. status is, status is not, etc.) */
const conditionType = ref<StatusBasedConditionType>(
  Object.values(StatusBasedConditionType)[0],
);

/**
 * Optional role or security group to scope the rule to. When set, the rule gets a
 * second LoginAccountRuleCondition alongside the workflow status one, so the rule
 * reads "if status is X AND the user (does not) belong to Y".
 *
 * The value is prefixed so one dropdown can offer both kinds: "role:Member" targets
 * the fixed SysOrgRoleName set every org has, "group:<guid>" targets an org-defined
 * security group. Orgs with no groups defined can still scope by role.
 */
const _conditionScope = ref<string>();
/** whether the scope segment is "belongs to" or "does not belong to" */
const groupConditionType = ref<LoginAccountRuleType>(
  LoginAccountRuleType.USER_BELONGS_TO,
);

/**
 * Guid of the login-account condition this editor is treating as the scope half of
 * the rule being edited. Captured so the update replaces exactly that condition and
 * leaves any standalone "If User" conditions on the same rule untouched.
 */
const _editingPairedScopeGuid = ref<string>();

const rolePrefix = "role:";
const groupPrefix = "group:";

/** Roles and groups, from the same source the section Access tab uses. */
const scopeOptionGroups = computed(() => [
  {
    label: "Roles",
    items:
      props.sectionAssignables?.roles?.map((r) => ({
        value: `${rolePrefix}${r.key}`,
        label: r.value ?? String(r.key),
      })) ?? [],
  },
  {
    label: "Groups",
    items:
      props.sectionAssignables?.groups?.map((g) => ({
        value: `${groupPrefix}${g.key}`,
        label: g.value ?? "",
      })) ?? [],
  },
]);

/** 'true' when the rule is scoped to a role or group */
const hasGroupCondition = computed<boolean>(() =>
  Boolean(_conditionScope.value),
);

/**
 * whether to use "and" or "or" logic for rules with multiple conditions.
 * A scoped rule is always "and" — "status is X or user is in Y" would fire
 * for everyone once the status matched, which is never what the author means.
 */
const trueIfAny = ref<boolean>(true);
const effectiveTrueIfAny = computed<boolean>(() =>
  hasGroupCondition.value ? false : trueIfAny.value,
);

/** available form items */
const formItems = computed<FormElementBase[]>(() =>
  props.form.components.filter((c) => c.guid !== undefined && !c.isArchived),
);

/** 'true' if the form item is not required for the condition */
const isRuleReadyToAdd = computed<boolean>(() =>
  Boolean(
    applicableElements.value &&
      ruleType.value &&
      conditionType.value &&
      _conditionWorkflowStatus.value,
  ),
);

/**
 * add a rule to the form
 * @param elementQuestionCode the question code of the element to add the rule to
 */
const addElementRuleToForm = (
  elementQuestionCode: string,
  elementGuid: string | undefined,
  newConditions: RuleConditionBase[],
) => {
  if (!isRuleReadyToAdd.value) {
    showError(
      "Developer Error: tried to add a rule that is missing some required fields",
    );
    return;
  }
  if (!ruleType.value || !elementQuestionCode) {
    showError(
      "Developer Error: tried to add a rule that is missing some required fields 2",
    );
    return;
  }

  // If we're editing an existing condition, find the original rule
  if (props.editingCondition) {
    // Find the original rule that contains the condition we're editing
    const originalRule = props.form.rules.find(
      (rule) => rule.guid === props.editingCondition?.rule.guid,
    );

    if (originalRule) {
      // Drop the condition being edited, plus the one login-account condition this
      // editor paired with it — the scope selector re-adds it if still wanted, so
      // clearing the selector removes the scoping instead of leaving it orphaned.
      //
      // Only the paired condition is dropped, identified by the guid captured when
      // editing began. A rule can also hold standalone login-account conditions added
      // through the "If User" editor, and those must survive an edit here.
      const editedGuid = props.editingCondition.condition.guid;
      const pairedScopeGuid = _editingPairedScopeGuid.value;
      const updatedRule = {
        ...originalRule,
        elementQuestionCode: elementQuestionCode,
        elementGuid: elementGuid ?? "",
        ruleType: ruleType.value,
        trueIfAny: effectiveTrueIfAny.value,
        conditions: [
          ...originalRule.conditions.filter(
            (c) => c.guid !== editedGuid && c.guid !== pairedScopeGuid,
          ),
          ...newConditions,
        ],
      };

      emit("update:rules", [
        ...props.form.rules.filter((r) => r !== originalRule),
        updatedRule,
      ]);
      emit("update:editing-condition", undefined);
      return;
    }
  }

  // check if the rule already exists
  const existingRule = props.form.rules.find(
    (rule) =>
      rule.elementQuestionCode === elementQuestionCode &&
      rule.ruleType === ruleType.value,
  );

  if (existingRule) {
    // Add the condition to the existing rule
    const updatedExistingRule = {
      ...existingRule,
      conditions: [...existingRule.conditions, ...newConditions],
      trueIfAny: effectiveTrueIfAny.value,
    };
    emit("update:rules", [
      ...props.form.rules.filter((r) => r !== existingRule),
      updatedExistingRule,
    ]);
    return;
  }

  // otherwise, create a new rule
  const newRule: FormElementRule = {
    // @ts-expect-error: Force the server to generate the Guid
    guid: undefined,
    elementQuestionCode: elementQuestionCode,
    elementGuid: elementGuid ?? "",
    ruleType: ruleType.value,
    trueIfAny: effectiveTrueIfAny.value,
    conditions: newConditions,
  };

  emit("update:rules", [...props.form.rules, newRule]);
};

const resetFormFields = () => {
  conditionType.value = StatusBasedConditionType.STATUS_IS;
  _conditionWorkflowStatus.value = undefined;
  ruleType.value = undefined;
  _applicableElementGuids.value = [];
  _conditionScope.value = undefined;
  _editingPairedScopeGuid.value = undefined;
  groupConditionType.value = LoginAccountRuleType.USER_BELONGS_TO;
  trueIfAny.value = true;
};

/**
 * builds the conditions for one element. Each element gets its own condition
 * objects so the server assigns distinct guids rather than sharing one instance.
 */
const buildConditions = (): RuleConditionBase[] => {
  const statusCondition: WorkflowStatusRuleCondition = {
    // @ts-expect-error: Force the server to generate the Guid
    guid: undefined,
    conditionType: ruleConditionType,
    type: conditionType.value,
    workflowGuid: props.workflowDefinition.guid,
    workflowStatus: _conditionWorkflowStatus.value ?? statusOptions.value[0].v,
  };

  const scope = _conditionScope.value;
  if (!scope) {
    return [statusCondition];
  }

  const isRole = scope.startsWith(rolePrefix);
  const scopeCondition: LoginAccountRuleCondition = {
    // @ts-expect-error: Force the server to generate the Guid
    guid: undefined,
    conditionType: RuleConditionType.LOGIN_ACCOUNT_BASED,
    type: groupConditionType.value,
    // Exactly one of these is set; the other stays undefined so the server stores null.
    role: isRole
      ? (scope.slice(rolePrefix.length) as SysOrgRoleName)
      : undefined,
    orgGroupGuid: isRole ? undefined : scope.slice(groupPrefix.length),
  };

  return [statusCondition, scopeCondition];
};

/**
 * iterates through the list of question codes selected and adds the rule to each element
 */
const handleAddRule = () => {
  if (!isRuleReadyToAdd.value || !conditionType.value) {
    showError(
      "Developer Error: tried to add a rule that is missing some required fields",
    );
    return;
  }

  // iterate through the list of element guids selected and add the rule to each element
  applicableElements.value.forEach((el) =>
    addElementRuleToForm(el.questionCode, el.guid, buildConditions()),
  );
  resetFormFields();
};

watch(
  () => props.editingCondition,
  () => {
    if (
      props.editingCondition &&
      isWorkflowStatusBasedCondition(props.editingCondition.condition)
    ) {
      _conditionWorkflowStatus.value =
        props.editingCondition.condition.workflowStatus ?? undefined;
      conditionType.value = props.editingCondition.condition.type;
      ruleType.value = props.editingCondition.rule.ruleType;
      _applicableElementGuids.value = [props.editingCondition.rule.elementGuid];
      trueIfAny.value = props.editingCondition.rule.trueIfAny;

      // Repopulate the scope segment from the condition paired with this one, so
      // editing the status half doesn't silently drop the scoping.
      const pairedScopeCondition = props.editingCondition.rule.conditions.find(
        isLoginAccountBasedCondition,
      );
      if (pairedScopeCondition?.role) {
        _conditionScope.value = `${rolePrefix}${pairedScopeCondition.role}`;
      } else if (pairedScopeCondition?.orgGroupGuid) {
        _conditionScope.value = `${groupPrefix}${pairedScopeCondition.orgGroupGuid}`;
      } else {
        _conditionScope.value = undefined;
      }
      _editingPairedScopeGuid.value = pairedScopeCondition?.guid;
      groupConditionType.value =
        pairedScopeCondition?.type ?? LoginAccountRuleType.USER_BELONGS_TO;
    } else {
      conditionType.value = StatusBasedConditionType.STATUS_IS;
      _conditionWorkflowStatus.value = undefined;
      ruleType.value = undefined;
      _applicableElementGuids.value = [];
      _conditionScope.value = undefined;
      _editingPairedScopeGuid.value = undefined;
      groupConditionType.value = LoginAccountRuleType.USER_BELONGS_TO;
      trueIfAny.value = true;
    }
  },
);
</script>

<template>
  <div class="rule-editor">
    <div>If Workflow</div>
    <el-select
      v-model="conditionType"
      size="small"
      placeholder="Condition"
      clearable
      style="max-width: 200px"
      filterable
    >
      <el-option
        v-for="condition in Object.values(StatusBasedConditionType)"
        :key="condition"
        :label="statusConditionDisplayNames[condition]"
        :value="condition"
      >
      </el-option>
    </el-select>

    <el-select
      v-model="_conditionWorkflowStatus"
      size="small"
      placeholder="Status"
      clearable
      filterable
    >
      <el-option
        v-for="kvp in statusOptions"
        :key="kvp.v"
        :label="kvp.l"
        :value="kvp.v"
      >
        {{ kvp.l }} - {{ kvp.v }}
      </el-option>
    </el-select>

    <span> Set </span>
    <el-select
      v-if="!editingCondition"
      v-model="_applicableElementGuids"
      size="small"
      placeholder="Form Item(s)"
      multiple
      collapse-tags
      collapse-tags-tooltip
      filterable
      clearable
    >
      <el-option
        v-for="item in formItems"
        :key="item.guid"
        :label="item.questionCode"
        :value="item.guid"
      >
      </el-option>
    </el-select>
    <el-select
      v-if="editingCondition"
      v-model="_applicableElementGuids"
      size="small"
      placeholder="Form Item(s)"
      collapse-tags
      collapse-tags-tooltip
      filterable
      clearable
    >
      <el-option
        v-for="item in formItems"
        :key="item.guid"
        :label="item.questionCode"
        :value="item.guid"
      >
      </el-option>
    </el-select>

    <span> to </span>

    <el-select
      v-model="ruleType"
      size="small"
      placeholder="Rule Type"
      filterable
      clearable
    >
      <el-option
        v-for="rt in Object.values(FormElementRuleType)"
        :key="rt"
        :label="rt"
        :value="rt"
      >
      </el-option>
    </el-select>

    <span> For </span>
    <div class="group-segment">
      <el-select
        v-model="groupConditionType"
        size="small"
        :disabled="!hasGroupCondition"
        filterable
        style="max-width: 170px"
      >
        <el-option
          v-for="lart in Object.values(LoginAccountRuleType)"
          :key="lart"
          :label="loginAccountConditionDisplayNames[lart]"
          :value="lart"
        >
        </el-option>
      </el-select>
      <el-select
        v-model="_conditionScope"
        size="small"
        placeholder="Anyone (optional)"
        clearable
        filterable
      >
        <el-option-group
          v-for="optionGroup in scopeOptionGroups"
          :key="optionGroup.label"
          :label="optionGroup.label"
        >
          <el-option
            v-for="item in optionGroup.items"
            :key="item.value"
            :label="item.label"
            :value="item.value"
          >
          </el-option>
        </el-option-group>
      </el-select>
    </div>

    <el-tooltip
      :disabled="!hasGroupCondition"
      content="A rule scoped to a security group always requires both the status and the group to match."
      placement="top"
    >
      <el-checkbox
        :model-value="effectiveTrueIfAny"
        :disabled="hasGroupCondition"
        size="small"
        label="True if any?"
        @update:model-value="(v: CheckboxValueType) => (trueIfAny = Boolean(v))"
      />
    </el-tooltip>
    <el-button
      v-if="!editingCondition"
      :disabled="!isRuleReadyToAdd"
      type="primary"
      @click="handleAddRule"
    >
      Add
    </el-button>
    <div v-else style="display: flex">
      <el-button
        type="primary"
        :disabled="!isRuleReadyToAdd"
        @click="handleAddRule"
      >
        Update
      </el-button>

      <el-button
        type="danger"
        plain
        @click="
          () => {
            emit('update:editing-condition', undefined);
            resetFormFields();
          }
        "
      >
        Cancel
      </el-button>
    </div>
  </div>
</template>

<style lang="less" scoped>
.rule-editor {
  display: grid;
  grid-template-columns:
    max-content repeat(2, auto)
    max-content auto max-content auto max-content auto repeat(2, max-content);
  gap: 20px;
  align-items: center;
  .answer {
    height: fit-content;
  }
  .group-segment {
    display: flex;
    gap: 8px;
    align-items: center;
  }
}
</style>
